<script lang="ts">
  /**
   * Creator Guide (public, #/submit-guide) — how Curation Duels work and how
   * creators get their songs into the game. Full rewrite for the Curation
   * Duels cutover: duels are system-generated themed contests fought with
   * catalog songs; the web is the creator portal, playing happens in the app.
   */
  import { getUser } from '$lib/auth.svelte';

  const user = $derived(getUser());

  const modes = [
    {
      name: 'Classic',
      desc: 'Head-to-head matchups — voters pick the better song of each pair, best win rate takes the crown.',
    },
    {
      name: 'League',
      desc: 'Every voter allocates a 10-point ballot across the lineup — most points wins.',
    },
    {
      name: 'Rapid',
      desc: 'Quickfire fire-or-pass verdicts, one song at a time — the highest fire rate wins.',
    },
    {
      name: 'Bracket',
      desc: 'Seeded knockout rounds — songs advance match by match until one is left standing.',
    },
  ];

  const creatorSteps = [
    {
      num: '1',
      title: 'Add a YouTube song',
      desc: 'Paste a YouTube link into your catalog. It gets a genre, a style, and an AI score — and it stays credited to you.',
    },
    {
      num: '2',
      title: 'Players claim it into duels',
      desc: 'Each player claims exactly one catalog song per duel, first-come-first-served. Your song can fight in many duels at once.',
    },
    {
      num: '3',
      title: 'Collect creator crowns',
      desc: 'When a song you added wins a duel, you earn the creator crown — trophies and ranking points, alongside the curator who claimed it.',
    },
  ];
</script>

<div class="guide">
  <div class="hero">
    <h1>Creator Guide</h1>
    <p class="hero-sub">
      Curation Duels are themed contests fought with catalog songs. Add music on the web, play in the app — and win
      crowns either way.
    </p>
    {#if user}
      <a href="#/catalog/add" class="btn-primary">Add a Song</a>
    {:else}
      <a href="#/register" class="btn-primary">Sign Up Free</a>
    {/if}
  </div>

  <!-- 1. How Curation Duels work -->
  <section class="section">
    <h2>How Curation Duels Work</h2>
    <p class="section-intro">
      The system generates themed duels around genres, styles, and editorial prompts — nobody creates them by hand.
      Players enter by claiming one catalog song each, then everyone votes in one of four game modes.
    </p>

    <div class="mode-grid">
      {#each modes as m (m.name)}
        <div class="mode-card">
          <h3>{m.name}</h3>
          <p>{m.desc}</p>
        </div>
      {/each}
    </div>

    <div class="tip-box">
      <strong>Anonymous until the reveal</strong>
      <p>
        Nobody sees who claimed which song while a duel is open or voting — votes go to the music, not the name.
        Identities are revealed when the duel ends.
      </p>
    </div>

    <div class="tip-box tip-box--gold">
      <strong>Dual crowns</strong>
      <p>
        Every duel crowns two winners: the <em>curator</em> who claimed the winning song, and the <em>creator</em> who added
        it to the catalog. Great taste and great additions both score.
      </p>
    </div>
  </section>

  <!-- 2. For creators -->
  <section class="section section--creator">
    <h2>For Creators</h2>
    <p class="section-intro">
      The web is your catalog workshop. Add YouTube songs you believe in — yours or anyone's — and let them compete.
    </p>

    <div class="steps">
      {#each creatorSteps as step (step.num)}
        <div class="step-card">
          <span class="step-num">{step.num}</span>
          <div>
            <h3>{step.title}</h3>
            <p>{step.desc}</p>
          </div>
        </div>
      {/each}
    </div>

    <div class="cta-buttons">
      {#if user}
        <a href="#/catalog/add" class="btn-primary">Add a Song</a>
      {:else}
        <a href="#/register" class="btn-primary">Sign Up to Add Songs</a>
      {/if}
    </div>
  </section>

  <!-- 3. For players -->
  <section class="section">
    <h2>For Players</h2>
    <p class="section-intro">
      Claiming songs and voting happen in the Beat Duel app. Browse the duels here on the web, then pick up your phone
      to play — claim a song into a themed duel, cast your votes, and chase the curator crown.
    </p>
    <div class="cta-buttons">
      <a href="#/duels" class="btn-primary">Browse Duels</a>
    </div>
  </section>
</div>

<style>
  .guide {
    max-width: 52rem;
    margin: 0 auto;
    padding-bottom: 3rem;
  }

  /* Hero */
  .hero {
    text-align: center;
    padding: 2rem 0 2.5rem;
  }
  .hero h1 {
    font-size: 1.75rem;
    margin: 0 0 0.75rem;
  }
  .hero-sub {
    color: var(--text-secondary);
    font-size: 1rem;
    max-width: 36rem;
    margin: 0 auto 1.5rem;
    line-height: 1.6;
  }

  /* Buttons */
  .btn-primary {
    display: inline-block;
    padding: 0.625rem 1.75rem;
    border-radius: var(--radius);
    background: linear-gradient(135deg, var(--pink), var(--purple));
    color: #fff;
    font-weight: 600;
    font-size: 0.9375rem;
    text-decoration: none;
    transition: opacity 0.2s;
  }
  .btn-primary:hover {
    opacity: 0.9;
  }

  /* Sections */
  .section {
    margin-top: 2.5rem;
  }
  h2 {
    font-size: 1.125rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--cyan);
    margin: 0 0 0.5rem;
  }
  .section-intro {
    color: var(--text-secondary);
    font-size: 0.9375rem;
    margin: 0 0 1.25rem;
    line-height: 1.5;
  }

  /* Vote-mode grid */
  .mode-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
  }
  .mode-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 1.25rem;
  }
  .mode-card h3 {
    margin: 0 0 0.375rem;
    font-size: 0.9375rem;
    color: var(--purple);
  }
  .mode-card p {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--text-secondary);
    line-height: 1.4;
  }

  /* Tip boxes */
  .tip-box {
    margin-top: 1.25rem;
    padding: 1rem 1.25rem;
    background: rgba(0, 255, 255, 0.05);
    border: 1px solid rgba(0, 255, 255, 0.15);
    border-radius: var(--radius);
  }
  .tip-box strong {
    color: var(--cyan);
    font-size: 0.875rem;
  }
  .tip-box p {
    margin: 0.375rem 0 0;
    font-size: 0.8125rem;
    color: var(--text-secondary);
    line-height: 1.5;
  }
  .tip-box--gold {
    background: rgba(255, 215, 0, 0.05);
    border-color: rgba(255, 215, 0, 0.2);
  }
  .tip-box--gold strong {
    color: var(--gold);
  }

  /* Creator section */
  .section--creator {
    padding: 1.5rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }
  .section--creator h2 {
    color: var(--pink);
  }

  /* Steps */
  .steps {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .step-card {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 1.25rem;
  }
  .step-num {
    flex-shrink: 0;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--pink), var(--purple));
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1rem;
    color: #fff;
  }
  .step-card h3 {
    margin: 0 0 0.25rem;
    font-size: 0.9375rem;
    color: var(--text);
  }
  .step-card p {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--text-secondary);
    line-height: 1.5;
  }

  /* CTA rows */
  .cta-buttons {
    margin-top: 1.25rem;
    display: flex;
    gap: 0.75rem;
    justify-content: center;
    flex-wrap: wrap;
  }

  @media (max-width: 600px) {
    .mode-grid {
      grid-template-columns: 1fr 1fr;
    }
  }
</style>
